/* eslint-disable react/no-unused-state */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';

import CommentWrapper from '../../components/CommentWrapper/CommentWrapper';
import FloatingMenu from '../../components/FloatingMenu/FloatingMenu';
import ShowMoreButton from '../../components/ShowMoreButton/ShowMoreButton';
import CommentDetails from '../../components/CommentDetails/CommentDetails';
import createMenu from './MenuConfig';
import CommentButtons from '../../components/CommentButtons/CommentButtons';
import ConnectedCommentInputContainer from '../CommentInputContainer/CommentInputContainer';
import { COMMENTS_CONTEXT_SOCIAL_FEED } from '../../utils/constants/commentConstants';

export class CommentContainer extends Component {
  state = {
    showCommentInput: false,
    cachedReplyPosted: null,
    updatedText: null
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const derivedState = {
      cachedReplyPosted: nextProps.replyPosted
    };
    if (
      nextProps.replyPosted === true &&
      prevState.cachedReplyPosted === false
    ) {
      derivedState.showCommentInput = false;
    }
    return derivedState;
  }

  handleShowMoreButtonClick = (e) => {
    e.stopPropagation();
    const { setShowMoreMenu: setShowMore, id } = this.props;
    setShowMore(id);
  };

  handleLikeButtonClick = () => {
    const {
      likedByMe,
      parentId,
      contentId,
      id: commentId,
      likeCommentRequest: likeComment,
      unlikeCommentRequest: unlikeComment,
      likeChildCommentRequest: likeChildComment,
      unlikeChildCommentRequest: unlikeChildComment
    } = this.props;

    if (parentId) {
      return likedByMe
        ? unlikeChildComment({ parentId, contentId, commentId })
        : likeChildComment({ parentId, contentId, commentId });
    }

    return likedByMe
      ? unlikeComment({ contentId, commentId })
      : likeComment({ contentId, commentId });
  };

  handleTranslateButtonClick = () => {
    const { isTranslationLoaded } = this.props;
    if (!isTranslationLoaded) {
      return this.submitCommentForTranslation();
    }
    return this.toggleTranslatedComment();
  };

  submitCommentForTranslation = () => {
    const {
      id: commentId,
      rootId,
      translateParentRequest: translateParent,
      translateChildRequest: translateChild
    } = this.props;

    return rootId
      ? translateChild({ commentId, rootId })
      : translateParent(commentId);
  };

  toggleTranslatedComment = () => {
    const {
      id: commentId,
      rootId,
      toggleParentTranslation: toggleParent,
      toggleChildTranslation: toggleChild
    } = this.props;

    return rootId
      ? toggleChild({ commentId, rootId })
      : toggleParent(commentId);
  };

  handleReplyButtonClick = () => {
    this.setState({ showCommentInput: true });
  };

  hideCommentInputContainer = () => {
    this.setState({ showCommentInput: false });
  };

  render() {
    const {
      body,
      dispatch,
      contentId,
      id: commentId,
      showMoreVisible,
      isReported,
      createdAt,
      commentDetails,
      likesCount,
      likedByMe,
      communityId,
      parentId,
      rootId,
      isDeleted,
      hasRepliesCollapse,
      repliesCollapsed,
      handleRepliesCollapseClick,
      repliesCount,
      canDelete,
      canReport,
      canEdit,
      authorIsUser,
      isEditing,
      userLanguage,
      commentLanguage,
      isTranslationLoading,
      isTranslated,
      translationError,
      commentZIndex,
      replyZIndex,
      mentionedUsers,
      commentsContext
    } = this.props;
    const { showCommentInput, updatedText } = this.state;
    const isReply = Boolean(parentId);
    const isCommentTranslatable = Boolean(
      // This logic compares shorter language codes
      // (e.g. 'en' instead of 'en-GB')
      // so that "View translation" doesn't show up
      // unnecessarily
      commentLanguage &&
        !userLanguage.includes(commentLanguage) &&
        !commentLanguage.includes(userLanguage)
    );
    const hasEllipsisMenuItems = canEdit || canDelete || canReport;

    const displayEllipsisButton =
      hasEllipsisMenuItems &&
      !isDeleted &&
      !isEditing &&
      commentsContext !== COMMENTS_CONTEXT_SOCIAL_FEED;

    return (
      <CommentWrapper
        isReply={isReply}
        isDeleted={isDeleted}
        hasRepliesCollapse={hasRepliesCollapse}
        repliesCollapsed={repliesCollapsed}
        id={commentId}
        // Used to assign z-indices to comments and replies.
        // No replyIndex indicates it's a parent
        // and gets the "top level" z-index.
        zIndex={replyZIndex || commentZIndex}
      >
        <CommentDetails
          body={isDeleted ? i18next.t('deletedComment') : body}
          details={commentDetails}
          contentId={contentId}
          commentId={commentId}
          createdAt={createdAt}
          authorIsUser={authorIsUser}
          isEditing={isEditing}
          parentId={parentId}
          mentionedUsers={mentionedUsers}
          communityId={communityId}
          commentsContext={commentsContext}
        />
        {displayEllipsisButton ? (
          <ShowMoreButton
            onClick={this.handleShowMoreButtonClick}
            isDisabled={isDeleted}
          />
        ) : (
          <div />
        )}
        {showMoreVisible && (
          <FloatingMenu
            menuItems={createMenu(dispatch, {
              commentId,
              contentId,
              isReported,
              canDelete,
              canEdit,
              rootId,
              canReport
            })}
          />
        )}
        {!isEditing && (
          <CommentButtons
            handleLikeButtonClick={this.handleLikeButtonClick}
            isLikedByUser={likedByMe}
            likesCount={likesCount}
            handleReplyButtonClick={this.handleReplyButtonClick}
            hasRepliesCollapse={!isReply && hasRepliesCollapse}
            repliesCollapsed={repliesCollapsed}
            handleRepliesCollapseClick={handleRepliesCollapseClick}
            repliesCount={repliesCount}
            isDeleted={isDeleted}
            isTranslatable={isCommentTranslatable}
            handleTranslateButtonClick={this.handleTranslateButtonClick}
            isTranslationLoading={isTranslationLoading}
            isTranslated={isTranslated}
            isTranslatedError={translationError}
            rootId={rootId}
            commentsContext={commentsContext}
          />
        )}
        {showCommentInput && (
          <ConnectedCommentInputContainer
            contentId={contentId}
            communityId={communityId}
            parentId={commentId}
            rootId={rootId}
            hideInputCallback={this.hideCommentInputContainer}
            inputValue={updatedText}
            zIndex={1}
            setInputValue={(val) => this.setState({ updatedText: val })}
          />
        )}
      </CommentWrapper>
    );
  }
}

CommentContainer.propTypes = {
  commentDetails: PropTypes.shape({
    name: PropTypes.string,
    avatar: PropTypes.string,
    repliedToName: PropTypes.string,
    userId: PropTypes.number
  }).isRequired,
  body: PropTypes.string,
  canDelete: PropTypes.bool,
  canReport: PropTypes.bool,
  canEdit: PropTypes.bool.isRequired,
  contentId: PropTypes.number.isRequired,
  createdAt: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  isReported: PropTypes.bool.isRequired,
  likedByMe: PropTypes.bool.isRequired,
  likeCommentRequest: PropTypes.func.isRequired,
  likeChildCommentRequest: PropTypes.func.isRequired,
  likesCount: PropTypes.number,
  parentId: PropTypes.number,
  rootId: PropTypes.number,
  setShowMoreMenu: PropTypes.func.isRequired,
  showMoreVisible: PropTypes.bool,
  unlikeCommentRequest: PropTypes.func.isRequired,
  communityId: PropTypes.number,
  isDeleted: PropTypes.bool,
  hasRepliesCollapse: PropTypes.bool,
  repliesCollapsed: PropTypes.bool,
  handleRepliesCollapseClick: PropTypes.func,
  unlikeChildCommentRequest: PropTypes.func.isRequired,
  repliesCount: PropTypes.number,
  authorIsUser: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool,
  userLanguage: PropTypes.string,
  commentLanguage: PropTypes.string,
  translateParentRequest: PropTypes.func.isRequired,
  translateChildRequest: PropTypes.func.isRequired,
  toggleParentTranslation: PropTypes.func.isRequired,
  toggleChildTranslation: PropTypes.func.isRequired,
  isTranslationLoading: PropTypes.bool.isRequired,
  isTranslationLoaded: PropTypes.bool.isRequired,
  isTranslated: PropTypes.bool.isRequired,
  translationError: PropTypes.bool.isRequired,
  commentZIndex: PropTypes.number,
  replyZIndex: PropTypes.number,
  mentionedUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      username: PropTypes.string,
      name: PropTypes.string
    })
  ),
  commentsContext: PropTypes.string
};

CommentContainer.defaultProps = {
  likesCount: 0,
  communityId: null,
  parentId: null,
  rootId: null,
  showMoreVisible: false,
  isDeleted: false,
  createdAt: null,
  hasRepliesCollapse: false,
  repliesCollapsed: false,
  handleRepliesCollapseClick: () => {},
  repliesCount: 0,
  isEditing: false,
  userLanguage: 'en-GB',
  commentZIndex: null,
  replyZIndex: null,
  mentionedUsers: null,
  body: '',
  canDelete: false,
  canReport: false,
  commentLanguage: null,
  commentsContext: null
};

export default CommentContainer;
